import { cn } from "@/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { FC } from "react";

const componentLabelVariants = cva(
  "text-[10px] font-semibold uppercase tracking-[0.5px]",
  {
    variants: {
      color: {
        blue: "text-type-accent",
        brown: "text-type-warm",
        gray: "text-type-2",
      },
    },
    defaultVariants: {
      color: "blue",
    },
  },
);

const subtextVariants = cva("", {
  variants: {
    subtextColor: {
      blue: "text-type-accent",
      brown: "text-type-warm",
      gray: "text-type-2",
    },
    defaultVariants: {
      subtextColor: "gray",
    },
  },
});

type CardLabelProps = {
  text: string;
  subtext?: string;
  suffix?: React.ReactNode;
} & VariantProps<typeof componentLabelVariants> &
  VariantProps<typeof subtextVariants> & {
    className?: string;
  };

function getLabelParts(text: string, subtext?: string) {
  const bracketedTextMatch = text.match(/^(.*?)(\s*\([^()]+\))$/);
  const mainText = bracketedTextMatch ? bracketedTextMatch[1].trimEnd() : text;
  const derivedSubtext = bracketedTextMatch?.[2].trimStart();

  if (subtext) {
    return {
      mainText,
      subtext:
        subtext.startsWith("(") && subtext.endsWith(")")
          ? subtext
          : `(${subtext})`,
    };
  }

  return {
    mainText,
    subtext: derivedSubtext,
  };
}

export const CardLabel: FC<CardLabelProps> = ({
  text,
  subtext,
  color,
  subtextColor,
  className = "",
  suffix,
}) => {
  const { mainText, subtext: resolvedSubtext } = getLabelParts(text, subtext);

  return (
    <span className={cn(componentLabelVariants({ color }), className)}>
      {mainText}
      {resolvedSubtext && (
        <span className={subtextVariants({ subtextColor })}>
          {resolvedSubtext}
        </span>
      )}

      {suffix && (
        <span className={cn(componentLabelVariants({ color }), "ml-1")}>
          {suffix}
        </span>
      )}
    </span>
  );
};
